import { createContext, useContext, useReducer, useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { db, getDoc, setDoc, doc } from "../firebaseConfig"; // Import Firestore functions
import { getAuth } from 'firebase/auth';

// Material Dashboard 2 React main context
const MaterialUI = createContext();
MaterialUI.displayName = "MaterialUIContext";

// Firestore functions
const savePreferences = async (userID, preferences) => {
  try {
    const preferencesRef = doc(db, "Users", userID, "preferences", "settings");
    await setDoc(preferencesRef, preferences, { merge: true });
  } catch (error) {
    console.error("Error saving preferences:", error);
  }
};

const loadPreferences = async (userID) => {
  try {
    const preferencesRef = doc(db, "Users", userID, "preferences", "settings");
    const docSnap = await getDoc(preferencesRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error("Error loading preferences:", error);
    return null;
  }
};

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV": return { ...state, transparentSidenav: action.value };
    case "WHITE_SIDENAV": return { ...state, whiteSidenav: action.value };
    case "SIDENAV_COLOR": return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR": return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR": return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR": return { ...state, openConfigurator: action.value };
    case "DIRECTION": return { ...state, direction: action.value };
    case "LAYOUT": return { ...state, layout: action.value };
    case "DARKMODE": return { ...state, darkMode: action.value };
    case "LOAD_PREFERENCES": return { ...state, ...action.preferences };
    default: throw new Error(`Unhandled action type: ${action.type}`);
  }
}

function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);
  const [userID, setUserID] = useState(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false); // Track if preferences are loaded

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserID(user.uid);
        const preferences = await loadPreferences(user.uid);
        if (preferences) {
          dispatch({ type: "LOAD_PREFERENCES", preferences });
        }
        setPreferencesLoaded(true); // Mark preferences as loaded
      } else {
        setUserID(null);
        setPreferencesLoaded(true); // Mark preferences as loaded even if user is not authenticated
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (preferencesLoaded && userID) { // Only save preferences if they have been loaded
      const preferences = {
        miniSidenav: controller.miniSidenav,
        transparentSidenav: controller.transparentSidenav,
        whiteSidenav: controller.whiteSidenav,
        sidenavColor: controller.sidenavColor,
        transparentNavbar: controller.transparentNavbar,
        fixedNavbar: controller.fixedNavbar,
        openConfigurator: controller.openConfigurator,
        direction: controller.direction,
        layout: controller.layout,
        darkMode: controller.darkMode,
      };
      savePreferences(userID, preferences);
    }
  }, [controller, userID, preferencesLoaded]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  if (!preferencesLoaded) {
    return null; // Or a loading spinner if desired
  }

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

function useMaterialUIController() {
  const context = useContext(MaterialUI);
  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }
  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
